import { useCallback, useEffect, useState } from "react";
import { api } from "@/api";
import { useNavigate, useParams } from "react-router-dom";
import useErrorHandling from "@/hooks/useErrorHandling";
import { toast } from "react-toastify";
import {
  AgreementForm,
  AgreementFormData,
} from "@/app/components/agreements/AgreementForm";
import { ModalError } from "@/app/components/modals/modalBuilder";
import { Agreement } from "@/api/agreements";
import { getIdFromRenterHandbook } from "@/utils/getIdFromHandbooks.ts";
import { loadHandbooks } from "@/utils/loadHandbooks";
import { normalizeDate, normalizeDatetime } from "@/utils";

export function EditAgreementPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const handleError = useErrorHandling();
  const [isLoading, setIsLoading] = useState(true);
  const [defaultValues, setDefaultValues] =
    useState<Partial<AgreementFormData>>();
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [modalErrors, setModalErrors] = useState<ModalError[]>([]);

  useEffect(() => {
    if (!id) return;

    api.agreements
      .get(Number(id))
      .then((agreement) => {
        setAgreement(agreement);
        setDefaultValues({
          number: agreement.number,
          renterId: agreement.renterId,
          description: agreement.description,
          requisiteId: String(agreement.requisiteId),
          startDate: normalizeDatetime(agreement.startDate),
          endDate: normalizeDatetime(agreement.endDate),
          deposit: agreement.deposit,
          renterRequisiteId: String(agreement.renterRequisiteId),
          meetingHours: agreement.meetingHours,
          scan: agreement.scan,
        });
      })
      .catch((err) => {
        const { errorMessage, parsedErrors } = handleError(err);
        setModalErrors(parsedErrors || []);
        toast.error(errorMessage);
        navigate("/dashboard/rentedOffices");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, navigate, handleError]);

  const onSubmit = useCallback(
    (
      data: AgreementFormData,
      officesData?: {
        offices: Array<{ officeId: number; price: number }>;
        workplaces: Array<{ workplaceId: number; price: number }>;
      } | null,
    ) => {
      if (!id || !agreement) return;

      // Используем актуальные данные офисов, если они переданы, иначе используем старые значения
      const offices = officesData?.offices || agreement.offices || [];

      getIdFromRenterHandbook(String(data.renterId))
        .then((renter) => {
          return api.agreements.update(Number(id), {
            number: data.number,
            offices: offices,
            renterId: Number(renter),
            description: data.description,
            requisiteId: Number(data.requisiteId) || null,
            startDate: normalizeDate(data.startDate),
            endDate: normalizeDate(data.endDate),
            deposit: data.deposit,
            renterRequisiteId: Number(data.renterRequisiteId) || null,
            meetingHours: data.meetingHours,
            scan: data.scan ?? undefined,
          });
        })
        .then(() => {
          loadHandbooks({ handbooks: ["agreements"] });
          toast.success("Договор аренды успешно обновлен");
          navigate("/dashboard/rentedOffices");
        })
        .catch((err: any) => {
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErrors(parsedErrors || []);
          toast.error(errorMessage);
        });
    },
    [id, agreement, navigate, handleError],
  );

  const onDelete = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      if (!id) {
        resolve(false);
        return;
      }

      api.agreements
        .delete(Number(id))
        .then(() => {
          loadHandbooks({ handbooks: ["agreements"] });
          toast.success("Договор аренды успешно удален");
          navigate("/dashboard/rentedOffices");
          resolve(true);
        })
        .catch((err: any) => {
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErrors(parsedErrors || []);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  }, [id, navigate, handleError]);

  if (isLoading) {
    return <div>Загрузка...</div>;
  }

  if (!agreement) {
    return null;
  }

  return (
    <>
      <h1 className="text-xl font-bold mb-4">
        Редактирование договора аренды №{agreement.number}
      </h1>
      <AgreementForm
        defaultValues={defaultValues}
        agreementId={agreement.id}
        initialOffices={agreement.offices || []}
        onSubmit={onSubmit}
        onDelete={onDelete}
        isEdit={true}
        modalErrors={modalErrors}
      />
    </>
  );
}
